import "@tento-ui/components/rich-text-editor/rich-text-editor";

import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { RichTextEditor } from "@tento-ui/components/rich-text-editor/rich-text-editor";
import { Documentation } from "@tento-ui/components/documentation/documentation";

@component({ name: "std-rich-chat-response" })
export class RichChatResponse extends LonaWebComponent {
  private $editor: RichTextEditor = this.hydrate("editor");

  /** A caller-supplied document sheet (the prose seam's `opts.style`). The
   * editor renders the document as slotted light DOM inside THIS shadow
   * root (`std-rich-text-editor` is `<slot>`), so this component's sheets
   * are the ones that reach it; keyed and idempotent, so a pooled block
   * re-adopts rather than stacking copies. */
  adoptDocumentStyle(key: string, style: Style.Compat): void {
    this.addStyle(key, style);
  }

  async bind({ markdown }: RichChatResponse.Props): Promise<this> {
    // Use Documentation.serde so pipe-table markdown renders as <table>.
    // The built-in prosemirror-markdown DEFAULT has no table support.
    const serde = await Documentation.serde.get();
    await this.$editor.bind({
      markdown,
      editable: false,
      serde,
    });
    return this;
  }

  static $styles = [
    Documentation.$docStyle,
    Documentation.$tableStyle,
    css`
      :host {
        display: block;
        width: 100%;
        --editor-max-width: none;
        --editor-padding: 0px;
      }

      h1 {
        --font-size: var(--h3-size);
        --margin-top: 24px;
        --margin-bottom: 16px;
        color: var(--text-pri);
      }
      h2 {
        --font-size: var(--h4-size);
        --margin-top: 20px;
        --margin-bottom: 14px;
        color: var(--text-pri);
      }
      h3 {
        --font-size: var(--h5-size);
        --margin-top: 16px;
        --margin-bottom: 12px;
        color: var(--text-pri);
      }
      h4,
      h5,
      h6 {
        --font-size: var(--text-12px);
        --margin-top: 12px;
        --margin-bottom: 10px;
        color: var(--text-pri);
      }
      p {
        --font-size: var(--chat-prose-size, var(--text-11px));
        --line-height-ratio: 1.4;
        --margin-bottom: 14px;
        color: var(--text-pri);
      }

      /* Code follows the PROSE, not its own fixed token. The old rules
         used the raw cap token (--text-10px) as a literal font-size, so a
         code chip rendered 10px inside a ~15.7px paragraph — three sizes
         on one surface once user bubbles joined. Now: pre converts the
         prose cap to a font size (the / 0.7 cap-to-font idiom), and both
         code forms take a RELATIVE size, so they track whatever prose
         metric the surface (or a look, via --chat-prose-size) states. */
      pre {
        padding: 12px;
        background: var(--bg-surface, #2a3342);
        border-radius: 8px;
        font-size: calc(var(--chat-prose-size, var(--text-11px)) / 0.7);
        line-height: 1.6;
        margin-block: 12px;
        overflow-x: auto;
      }

      pre code {
        white-space: pre;
        font-size: var(--chat-code-size, 0.9em);
        font-family: "SF Mono", "Fira Code", "Fira Mono", Menlo, Consolas,
          monospace;
        color: var(--text-pri);
        background: none;
        padding: 0;
        border-radius: 0;
      }

      code {
        font-size: var(--chat-code-size, 0.9em);
        font-family: "SF Mono", "Fira Code", "Fira Mono", Menlo, Consolas,
          monospace;
        background: var(--bg-surface, rgba(0, 0, 0, 0.06));
        padding: 2px 6px;
        border-radius: 4px;
        color: var(--text-pri);
      }

      /* The docs sheet (adopted above) paints blockquote as an ELEVATED
         PANEL — var(--bg-elevated) with padding and a radius — which is a
         documentation affordance: on a chat look that never defines that
         token the panel came out light under light ink and the quote was
         unreadable. A chat quote is the messenger idiom instead: a bar,
         an indent, dimmer ink — and the inner tags follow the quote's ink
         rather than re-asserting --text-pri. */
      blockquote {
        background: none;
        border-radius: 0;
        border-left: 3px solid var(--divider, #ccc);
        margin-left: 0;
        margin-right: 0;
        padding: 0 0 0 12px;
        color: var(--text-sec);
      }
      blockquote :is(p, li) {
        color: inherit;
      }

      /* Links follow the PROSE, exactly as code does: the base typography
         sheet tokens anchors at --p-size, which is not what this surface
         sizes its paragraphs at — an anchor rendered a different size than
         its own sentence. Re-token to the same metric, and let a look
         restate the ink (--chat-link-ink) — product default otherwise. */
      a {
        --font-size: var(--chat-prose-size, var(--text-11px));
        color: var(--chat-link-ink, var(--link, var(--blue)));
        text-decoration: underline;
        text-underline-offset: 2px;
      }

      ul,
      ol {
        padding-left: 24px;
      }

      /* The editor HOST ships overflow-y auto + a stable scrollbar gutter —
         right for the live editing surface it was built as, wrong for a
         read-only response: baseline-capheight typography trims the first
         line with a negative margin, the content overflows its box by a
         few pixels, and every markdown bubble minted a scrollbar. A chat
         response never scrolls itself — the thread scrolls. */
      #editor {
        padding-block: var(--chat-markdown-inset, 8px);
        overflow: visible;
        scrollbar-gutter: auto;
      }
    `,
  ];

  static $html =
    html`<std-rich-text-editor id="editor"></std-rich-text-editor>`;
}

export namespace RichChatResponse {
  export type Props = {
    markdown: string;
  };
}
